import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { ChevronDown, ChevronRight, MapPinned, RefreshCcw, Search, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFavoriteTrackTraceLocations, getTrackTraceLocations, updateFavoriteTrackTraceLocations } from "../trackTrace/api";
import { TrackTraceLocation } from "../trackTrace/types";
import "../assets/styles/track-trace.css";

const TrackTraceLocationsPage: React.FC = () => {
    const { i18n } = useTranslation();
    const t = useCallback((key: string, options?: object) => (i18n.t as unknown as (key: string, options?: object) => string)(key, options), [i18n]);
    const navigate = useNavigate();
    const [locations, setLocations] = useState<TrackTraceLocation[]>([]);
    const [favorites, setFavorites] = useState<string[]>([]);
    const [q, setQ] = useState('');
    const [assetTypeId, setAssetTypeId] = useState('');
    const [assetTypeOptions, setAssetTypeOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [expandedParentIds, setExpandedParentIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (filters: { q: string; asset_type_id: string }) => {
        setLoading(true);
        setError('');
        try {
            const [locationRows, favoriteRows] = await Promise.all([
                getTrackTraceLocations(filters),
                getFavoriteTrackTraceLocations().catch(() => ({ location_ids: [] })),
            ]);
            setLocations(locationRows);
            setFavorites((favoriteRows.location_ids || []).map(String));
            setAssetTypeOptions((current) => {
                const next = new Map(current.map((item) => [item.id, item]));
                locationRows.forEach((location: TrackTraceLocation) => {
                    (location.asset_type_counts || []).forEach((item) => {
                        if (item.asset_type_id) {
                            next.set(String(item.asset_type_id), { id: String(item.asset_type_id), name: item.asset_type_name || t('track_trace.common.unknown') });
                        }
                    });
                });
                return Array.from(next.values()).sort((a, b) => a.name.localeCompare(b.name));
            });
        } catch (err) {
            setError(t('track_trace.locations.errors.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load({ q: '', asset_type_id: '' });
    }, [load]);

    const toggleFavorite = async (id: string) => {
        const next = favorites.includes(id) ? favorites.filter((item) => item !== id) : [...favorites, id];
        setFavorites(next);
        try {
            await updateFavoriteTrackTraceLocations(next);
        } catch {
            setFavorites(favorites);
        }
    };

    const groupedLocations = Array.from(locations.reduce((groups, location) => {
        const mapId = location.map_context?.id ? String(location.map_context.id) : 'without-map';
        const existing = groups.get(mapId) || {
            id: mapId,
            name: location.map_context?.name || t('track_trace.locations.unassigned_floor_plan'),
            children: [] as TrackTraceLocation[],
            assetCount: 0,
            visibleAssetCount: 0,
        };

        existing.children.push(location);
        existing.assetCount += location.asset_count || 0;
        existing.visibleAssetCount += location.visible_asset_count || 0;
        groups.set(mapId, existing);

        return groups;
    }, new Map<string, { id: string; name: string; children: TrackTraceLocation[]; assetCount: number; visibleAssetCount: number }>()).values())
        .map((group) => ({
            ...group,
            children: [...group.children].sort((a, b) => Number(favorites.includes(String(b.id))) - Number(favorites.includes(String(a.id))) || (a.path || a.name).localeCompare(b.path || b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const locationCount = groupedLocations.reduce((count, group) => count + group.children.length, 0);
    const applyFilters = () => load({ q, asset_type_id: assetTypeId });
    const toggleParent = (parentId: string) => {
        setExpandedParentIds((current) => current.includes(parentId)
            ? current.filter((id) => id !== parentId)
            : [...current, parentId]
        );
    };
    const openLocationMap = (location: TrackTraceLocation) => {
        const mapId = location.map_context?.id;
        navigate(`/track-trace/locations/${location.id}/map${mapId ? `?map_id=${encodeURIComponent(String(mapId))}` : ''}`);
    };

    return (
        <div className="track-trace-page">
            <div className="track-trace-header">
                <div>
                    <h1>{t('track_trace.title')}</h1>
                    <p>{t('track_trace.locations.subtitle')}</p>
                </div>
                <Button variant="light" size="sm" onClick={() => load({ q, asset_type_id: assetTypeId })} aria-label={t('track_trace.locations.actions.refresh_locations')}>
                    <RefreshCcw size={18} />
                </Button>
            </div>

            <div className="track-trace-location-filters">
                <InputGroup>
                    <InputGroup.Text><Search size={17} /></InputGroup.Text>
                    <Form.Control
                        value={q}
                        placeholder={t('track_trace.locations.filters.search_locations')}
                        onChange={(event) => setQ(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
                    />
                </InputGroup>
                <Form.Select value={assetTypeId} onChange={(event) => setAssetTypeId(event.target.value)} aria-label={t('track_trace.filters.asset_type_filter')}>
                    <option value="">{t('track_trace.locations.filters.all_asset_types')}</option>
                    {assetTypeOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Form.Select>
                <Button variant="outline-primary" onClick={applyFilters}>{t('track_trace.actions.apply')}</Button>
            </div>

            {loading && <div className="track-trace-center"><Spinner animation="border" /></div>}
            {error && <Alert variant="danger">{error}</Alert>}
            {!loading && !error && locationCount === 0 && <Alert variant="light">{t('track_trace.locations.empty')}</Alert>}

            <div className="track-trace-location-list">
                {groupedLocations.map((group) => (
                    <div className="track-trace-location-group" key={group.id}>
                        <button className="location-parent-button" onClick={() => toggleParent(group.id)}>
                            <MapPinned size={22} />
                            <span>
                                <strong>{group.name}</strong>
                                <small>{t('track_trace.locations.group_summary', { locationCount: group.children.length, assetCount: group.assetCount })}</small>
                            </span>
                            <span className="location-count-pill">{group.children.length}</span>
                            {expandedParentIds.includes(group.id) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </button>

                        {expandedParentIds.includes(group.id) && (
                            <div className="track-trace-child-list">
                                {group.children.map((location) => (
                                    <div className="track-trace-location-row" key={location.list_key || location.id}>
                                        <Button variant="link" className="favorite-button" onClick={() => toggleFavorite(String(location.id))} aria-label={t('track_trace.locations.actions.toggle_favorite')}>
                                            <Star size={20} fill={favorites.includes(String(location.id)) ? 'currentColor' : 'none'} />
                                        </Button>
                                        <button className="location-main-button" onClick={() => openLocationMap(location)}>
                                            <MapPinned size={22} />
                                            <span>
                                                <strong>{location.name}</strong>
                                                <small>{t('track_trace.locations.shown_on', {
                                                    mapName: location.map_context?.name || t('track_trace.locations.selected_floor_plan'),
                                                    zoneText: location.map_context?.zone_count ? t('track_trace.locations.zone_suffix', { count: location.map_context.zone_count }) : '',
                                                })}</small>
                                                <small>{[location.type, location.floor].filter(Boolean).join(' - ') || t('track_trace.common.location')}</small>
                                                <small>{t('track_trace.locations.asset_visibility', {
                                                    assetCount: location.asset_count || 0,
                                                    visibleText: location.visible_asset_count !== undefined ? t('track_trace.locations.visible_suffix', { count: location.visible_asset_count }) : '',
                                                })}</small>
                                            </span>
                                            <span className="location-count-pill">{location.asset_count || 0}</span>
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TrackTraceLocationsPage;
